import type { FC } from 'react';

import { cn } from '@/utils/cn.util';

interface AvatarProps {
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeStyles: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: 'h-7 w-7 text-[10px]',
  md: 'h-8 w-8 text-[11px]',
  lg: 'h-10 w-10 text-xs',
  xl: 'h-16 w-16 text-xl',
};

/**
 * Deterministic gradient for a given name. Same input → same colors across renders/sessions
 * so initials read as distinct people in tables without storing avatars server-side.
 */
const GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ['#2A66D9', '#3E86E8'], // C.primary  -> C.brand
  ['#1B47A6', '#2A66D9'], // C.primaryD -> C.primary
  ['#12936A', '#16B07F'], // C.green    -> derived
  ['#D93B2C', '#E4614F'], // C.coral    -> derived
  ['#D9871A', '#E0A41F'], // C.amber    -> C.coin
  ['#C9457E', '#DD6A9B'], // C.pink     -> derived
  ['#3E86E8', '#6BA5EF'], // C.brand    -> derived
  ['#8A5E12', '#E0A41F'], // C.coinD    -> C.coin
];

const hash = (input: string): number => {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const Avatar: FC<AvatarProps> = ({ name, src, size = 'md', className }) => {
  const initials = initialsOf(name) || '·';
  const [from, to] = GRADIENTS[hash(name) % GRADIENTS.length]!;

  if (src) {
    // Render the initials gradient underneath and overlay the image; if the image
    // fails to load, hide it so the initials show through (no broken-image icon).
    return (
      <div className={cn('relative shrink-0 overflow-hidden rounded-full', sizeStyles[size], className)}>
        <div
          className="absolute inset-0 flex items-center justify-center font-bold text-white"
          style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}
          aria-hidden
        >
          {initials}
        </div>
        <img
          src={src}
          alt={name}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          className="absolute inset-0 h-full w-full rounded-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-bold text-white',
        sizeStyles[size],
        className,
      )}
      style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}
      aria-label={name}
    >
      {initials}
    </div>
  );
};

export default Avatar;
